import { themeStore } from "@/stores/theme";
import { Dictionary } from "@/types/base";
import { isFirefox } from "@/utils/ua";
import { BoxProps } from "@chakra-ui/react";
import { v4 } from "uuid";

/**
 * 封装了滚动条样式
 * @param options
 * !!! 旨在解决滚动容器宽度不被滚动条宽度影响的问题
 */
export interface ScrollBarOptions {
  /*
   * 可以使滚动容器横向自动伸缩，在非 Firefox 浏览器下默认为 true。
   * 注意！该属性会把容器的 > div 的 width 设置为 auto
   */
  autoMargin?: boolean;
  /* 默认 overflow 是 scroll，overflowAuto 设置为 true 的话，则 overflowY: auto */
  overflowAuto?: boolean;
  /* 除了 div 外，其他的子元素，想要达到相同的缩放自如的效果，需要手动传
   * 以选择器的格式！
   * 比如 分割线 hr，现默认包含 hr。
   * 注意！这个属性传入的元素的 width 不会设置为 auto
   */
  autoMarginEl?: string[];
  /**
   * 容器是否可以滚动（容器内容高度 超出 容器高度）
   * 这个属性可以通过 useScrollBar 获取~
   * 如果 scrollable 为 false，overflowY 为 hidden!
   */
  scrollable?: boolean;
  /**
   * 如果想使用 autoMargin，但直接子元素 div 的宽度又不能是 auto，可以通过这个改掉
   * 如果 autoMargin 为 false，该属性不生效
   */
  childWidth?: string;
  scrollBarColor?: string;
  scrollBarWidth?: number;
  scrollBarBorderRadius?: number;
}
function getScrollBarProps(options?: ScrollBarOptions): BoxProps & { "data-scroll-name": string } {
  const {
    scrollable = true,
    autoMargin = isFirefox() ? false : true,
    overflowAuto = false,
    childWidth = "auto",
    autoMarginEl = ["hr"],
    scrollBarColor = themeStore.styles.scrollBarColor,
    scrollBarWidth = 8,
    scrollBarBorderRadius = 0,
  } = options ?? {};

  const scrollBarHoverColor = "#565656";
  const scrollBarActiveColor = "#8c8c8c";

  const childDivProps = () => {
    if (!scrollable) return {};

    return autoMargin
      ? {
          "& > div": {
            width: childWidth,
          },
          "&:hover > div": {
            marginRight: `-${scrollBarWidth}px`,
          },
        }
      : {};
  };

  const containerMarginProps = () => {
    const defaultContainerProps = {
      "&:hover": {
        scrollbarColor: `${scrollBarColor} transparent`,
        "&::-webkit-scrollbar-thumb": {
          color: scrollBarColor,
          background: scrollBarColor,
        },
      },
    };
    if (!scrollable) {
      return defaultContainerProps;
    }
    return autoMargin
      ? {
          marginRight: `-${scrollBarWidth}px`,
          "&:hover": {
            marginRight: "0px",
            scrollbarColor: `${scrollBarColor} transparent`,

            "&::-webkit-scrollbar-thumb": {
              color: scrollBarColor,
              background: scrollBarColor,
            },
          },
        }
      : defaultContainerProps;
  };

  const extraChildProps = () => {
    if (!scrollable) return {};
    return autoMarginEl?.length
      ? autoMarginEl.reduce<Dictionary>((props, selector) => {
          const selectorStr = `&:hover > ${selector}`;
          props[selectorStr] = {
            marginRight: `-${scrollBarWidth}px`,
          };
          return props;
        }, {})
      : {};
  };

  const overflowY = () => {
    if (scrollable) {
      return overflowAuto ? "auto" : "scroll";
    } else {
      return "hidden";
    }
  };

  return {
    overflow: "auto",
    overflowX: "hidden",
    overflowY: overflowY(), // 为了明确右边滚动条占位
    transition: "color .3s ease",
    css: {
      ...childDivProps(),
      ...containerMarginProps(),
      ...extraChildProps(),
      /* 滚动条样式标准设置 */
      scrollbarColor: "transparent transparent",
      scrollbarWidth: "thin",
      /* 滚动条整体部分 */
      "&::-webkit-scrollbar": {
        width: `${scrollBarWidth}px`,
        borderRadius: "0px",
      },
      /* 滑块 */
      "&::-webkit-scrollbar-thumb": {
        boxShadow: "inset 0 0 0 10px",
        borderRadius: `${scrollBarBorderRadius}px`,
        color: "transparent",
        background: "transparent",
        "&:hover": {
          color: scrollBarHoverColor,
          background: scrollBarHoverColor,
          scrollbarColor: `${scrollBarHoverColor} transparent`,
        },
        "&:active": {
          color: scrollBarActiveColor,
          backgroundColor: scrollBarActiveColor,
          scrollbarColor: `${scrollBarActiveColor} transparent`,
        },
      },
    },
    "data-scroll-name": `scroll-container--${v4()}`,
  };
}

export default getScrollBarProps;
